'use client';

import { createNavigation } from 'next-intl/navigation';
import { routing } from './routing';
import { useSearchParams } from 'next/navigation';
import { forwardRef, useMemo, createElement, type ComponentProps } from 'react';
import {
  mergeHrefWithParams,
  type PreserveParamsMode,
} from '@/shared/utils/url-params';

// 원본 navigation API 생성
const {
  Link: OriginalLink,
  redirect: originalRedirect,
  usePathname: originalUsePathname,
  useRouter: originalUseRouter,
  getPathname: originalGetPathname,
} = createNavigation(routing);

// 원본 타입 추출
type OriginalLinkProps = ComponentProps<typeof OriginalLink>;
type OriginalRouter = ReturnType<typeof originalUseRouter>;
type RouterHref = Parameters<OriginalRouter['push']>[0];
type OriginalRouterOptions = Parameters<OriginalRouter['push']>[1];

// usePathname과 getPathname은 그대로 export
export const usePathname = originalUsePathname;
export const getPathname = originalGetPathname;

/**
 * 보존 옵션 → 보존 모드. preserveAllParams 가 preserveAttributionParams 보다 우선한다.
 */
export const resolvePreserveMode = (options?: {
  preserveAllParams?: boolean;
  preserveAttributionParams?: boolean;
}): PreserveParamsMode => {
  if (options?.preserveAllParams) return 'all';
  if (options?.preserveAttributionParams) return 'attribution';
  return 'atTrackId';
};

/**
 * Custom Link Props
 * preserveAllParams가 true면 모든 쿼리 파라미터를 유지하고,
 * preserveAttributionParams가 true면 광고 attribution 파라미터만 유지하며,
 * 둘 다 false(기본값)면 atTrackId만 유지합니다.
 */
type CustomLinkProps = OriginalLinkProps & {
  preserveAllParams?: boolean;
  preserveAttributionParams?: boolean;
};

/**
 * atTrackId 자동 유지 기능이 추가된 Link 컴포넌트
 *
 * @example
 * // 기본: atTrackId만 유지
 * <Link href="/home">홈으로</Link>
 *
 * // 모든 파라미터 유지
 * <Link href="/payment" preserveAllParams>결제</Link>
 *
 * // 광고 attribution(utm_*·fbclid·igshid·atTrackId)만 유지
 * <Link href="/auth/signin" preserveAttributionParams>가입</Link>
 */
export const Link = forwardRef<HTMLAnchorElement, CustomLinkProps>(
  function Link(props, ref) {
    const {
      href,
      preserveAllParams = false,
      preserveAttributionParams = false,
      ...restProps
    } = props;
    const searchParams = useSearchParams();
    const mode = resolvePreserveMode({
      preserveAllParams,
      preserveAttributionParams,
    });
    const mergedHref = mergeHrefWithParams(href, searchParams, mode);

    return createElement(OriginalLink, {
      ref,
      href: mergedHref,
      ...restProps,
    });
  },
);

/**
 * Router 옵션 타입 (preserveAllParams 추가)
 */
type NavigationOptions = OriginalRouterOptions & {
  preserveAllParams?: boolean;
  preserveAttributionParams?: boolean;
};

/**
 * atTrackId 자동 유지 기능이 추가된 useRouter 훅
 *
 * @example
 * const router = useRouter();
 *
 * // 기본: atTrackId만 유지
 * router.push('/home');
 *
 * // 모든 파라미터 유지
 * router.push('/payment', { preserveAllParams: true });
 */
export function useRouter() {
  const router = originalUseRouter();
  const searchParams = useSearchParams();

  // searchParams를 문자열로 변환하여 안정적인 의존성 생성
  const searchParamsString = searchParams?.toString() ?? '';

  return useMemo(
    () => ({
      ...router,
      push: (href: RouterHref, options?: NavigationOptions) => {
        const mode = resolvePreserveMode(options);
        const mergedHref = mergeHrefWithParams(href, searchParams, mode);
        return router.push(mergedHref, options);
      },
      replace: (href: RouterHref, options?: NavigationOptions) => {
        const mode = resolvePreserveMode(options);
        const mergedHref = mergeHrefWithParams(href, searchParams, mode);
        return router.replace(mergedHref, options);
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router, searchParamsString],
  );
}

/**
 * atTrackId 자동 유지 기능이 추가된 redirect 함수
 * 서버 컴포넌트에서 사용할 수 있도록 headers에서 URL 정보를 추출합니다.
 *
 * @example
 * // 기본: atTrackId만 유지
 * redirect('/login');
 *
 * // 모든 파라미터 유지
 * redirect('/dashboard', { preserveAllParams: true });
 */
type RedirectHref = Parameters<typeof originalRedirect>[0];

export function redirect(
  href: RedirectHref,
  options?: {
    preserveAllParams?: boolean;
    preserveAttributionParams?: boolean;
  },
): ReturnType<typeof originalRedirect> {
  if (typeof window === 'undefined') {
    return originalRedirect(href);
  }

  const mode = resolvePreserveMode(options);
  const searchParams = new URLSearchParams(window.location.search);
  const mergedHref = mergeHrefWithParams(href, searchParams, mode);

  return originalRedirect(mergedHref);
}
